import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { useAutenticacao } from '../lib/auth';
import {
  buscarMembros,
  buscarPessoas,
  criarPessoa,
  definirSenha,
  salvarMembro,
  salvarPessoa,
  NOME_DO_PAPEL,
  type MembroLista,
  type Pessoa,
} from '../lib/escalas';
import { Alerta, Botao, Campo, Cartao, Modal } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import { gerarSenha } from '../lib/senha';
import Layout from '../componentes/Layout';

const PAPEIS = ['admin', 'editor', 'viewer', 'lider', 'lider_adm', 'voluntario', 'membro'] as const;

export default function Equipe() {
  const { usuario } = useAutenticacao();
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [membros, setMembros] = useState<MembroLista[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [trocandoSenhaDe, setTrocandoSenhaDe] = useState<Pessoa | null>(null);
  const { confirmar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([buscarPessoas(), buscarMembros()]);
      setPessoas(p.pessoas);
      setMembros(m.membros);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as pessoas.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function mudar(pessoa: Pessoa, mudancas: Parameters<typeof salvarPessoa>[1], texto: string) {
    setErro(null);
    setAviso(null);
    try {
      await salvarPessoa(pessoa.id, mudancas);
      setAviso(texto);
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    }
  }

  async function salvarAniversario(membro: MembroLista, nascimento: string | null) {
    setErro(null);
    setAviso(null);
    try {
      await salvarMembro(membro.id, { nascimento });
      setAviso(
        nascimento
          ? `Aniversario de ${membro.nome} salvo.`
          : `Data de ${membro.nome} removida.`,
      );
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar a data.');
    }
  }

  async function alternarAtivo(p: Pessoa) {
    const ok = await confirmar({
      titulo: p.ativo ? `Desativar ${p.nome}?` : `Reativar ${p.nome}?`,
      texto: p.ativo
        ? 'A pessoa perde o acesso na hora, mesmo com a sessão aberta. O histórico dela continua guardado.'
        : 'A pessoa volta a conseguir entrar com a senha dela.',
      confirmar: p.ativo ? 'Desativar' : 'Reativar',
    });
    if (ok) {
      void mudar(
        p,
        { ativo: !p.ativo },
        p.ativo ? `${p.nome} foi desativado.` : `${p.nome} foi reativado.`,
      );
    }
  }

  // Estado, nao controle: quem vai servir e cadastrado em Escalas, que ja cria a
  // conta ligada. Aqui a tela so diz se da para escalar.
  const situacaoEscala = (p: Pessoa) => (
    p.podeSerEscalado ? (
      <span className="text-xs text-tinta-900">
        pode ser escalado{p.membroNome ? ` · ${p.membroNome}` : ''}
      </span>
    ) : !p.membroId ? (
      <span className="text-xs text-tinta-400">
        sem cadastro de membro
        {p.papel === 'membro' || p.papel === 'voluntario'
          ? ' — não vê escala nem pode ser escalado'
          : ''}
      </span>
    ) : p.papel === 'membro' ? (
      <span className="text-xs text-amber-700">
        papel membro não serve — suba para voluntário para poder escalar
      </span>
    ) : (
      <span className="text-xs text-tinta-400">conta desativada</span>
    )
  );

  const ultimoAcesso = (p: Pessoa) =>
    p.ultimoLogin
      ? new Intl.DateTimeFormat('pt-BR', {
          timeZone: 'America/Sao_Paulo',
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(p.ultimoLogin))
      : 'nunca entrou';

  return (
    <Layout
      titulo="Equipe e acessos"
      subtitulo="Quem entra no sistema, com qual papel, e ligado a qual cadastro."
      acoes={<Botao onClick={() => setCriando(true)}>+ Nova pessoa</Botao>}
    >
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}
      {aviso && (
        <div className="mb-4 max-w-2xl">
          <Alerta tipo="ok">{aviso}</Alerta>
        </div>
      )}

      {/* celular: um cartao por pessoa, com tudo visivel (a tabela larga escondia papel e acoes) */}
      <div className="space-y-2 md:hidden">
        {pessoas.map((p) => (
          <Cartao key={p.id} className={`p-4 ${p.ativo ? '' : 'opacity-50'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="block truncate font-medium text-tinta-900">{p.nome}</span>
                <span className="block truncate text-xs text-tinta-400">{p.email}</span>
              </div>
              {!p.ativo && (
                <span className="shrink-0 rounded bg-tinta-100 px-2 text-xs text-tinta-600">desativado</span>
              )}
            </div>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs text-tinta-400">Papel</span>
              <select
                value={p.papel}
                onChange={(e) =>
                  void mudar(p, { papel: e.target.value }, `${p.nome} agora e ${NOME_DO_PAPEL[e.target.value]}.`)
                }
                className="w-full rounded-lg border border-tinta-200 bg-white px-2 py-2 text-sm"
              >
                {PAPEIS.map((papel) => (
                  <option key={papel} value={papel}>
                    {NOME_DO_PAPEL[papel]}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-2">{situacaoEscala(p)}</div>
            <p className="mt-1 text-xs text-tinta-400">Último acesso: {ultimoAcesso(p)}</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setTrocandoSenhaDe(p)}
                className="rounded-lg border border-tinta-200 px-2.5 py-2 text-sm text-tinta-700 hover:bg-tinta-100"
              >
                Definir senha
              </button>
              <button
                onClick={() => void alternarAtivo(p)}
                disabled={usuario?.id === p.id}
                className="rounded-lg border border-tinta-200 px-2.5 py-2 text-sm text-tinta-700 hover:bg-tinta-100 disabled:opacity-40"
              >
                {p.ativo ? 'Desativar' : 'Reativar'}
              </button>
            </div>
          </Cartao>
        ))}
      </div>

      <Cartao className="hidden overflow-x-auto p-0 md:block">
        <table className="w-full min-w-[52rem] text-sm">
          <thead>
            <tr className="border-b border-tinta-200 text-left text-xs uppercase tracking-wide text-tinta-400">
              <th className="px-5 py-3">Pessoa</th>
              <th className="px-3 py-3">Papel</th>
              <th className="px-3 py-3">Escala</th>
              <th className="px-3 py-3">Último acesso</th>
              <th className="px-3 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {pessoas.map((p) => (
              <tr key={p.id} className={`border-b border-tinta-100 ${p.ativo ? '' : 'opacity-50'}`}>
                <td className="px-5 py-3">
                  <span className="block font-medium text-tinta-900">{p.nome}</span>
                  <span className="block text-xs text-tinta-400">{p.email}</span>
                  {!p.ativo && (
                    <span className="mt-1 inline-block rounded bg-tinta-100 px-2 text-xs text-tinta-600">
                      desativado
                    </span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <select
                    value={p.papel}
                    onChange={(e) =>
                      void mudar(p, { papel: e.target.value }, `${p.nome} agora e ${NOME_DO_PAPEL[e.target.value]}.`)
                    }
                    className="rounded-lg border border-tinta-200 bg-white px-2 py-1 text-sm"
                  >
                    {PAPEIS.map((papel) => (
                      <option key={papel} value={papel}>
                        {NOME_DO_PAPEL[papel]}
                      </option>
                    ))}
                  </select>
                </td>
                {/* Estado, nao controle: quem vai servir e cadastrado em Escalas, que
                    ja cria a conta ligada. Aqui a tela so diz se da para escalar — e
                    o vinculo sozinho nao basta, o papel tambem precisa servir. */}
                <td className="px-3 py-3">
                  {situacaoEscala(p)}
                </td>
                <td className="px-3 py-3 text-xs text-tinta-400">
                  {ultimoAcesso(p)}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setTrocandoSenhaDe(p)}
                      className="rounded-lg border border-tinta-200 px-2.5 py-1 text-xs text-tinta-700 hover:bg-tinta-100"
                    >
                      Definir senha
                    </button>
                    <button
                      onClick={() => void alternarAtivo(p)}
                      disabled={usuario?.id === p.id}
                      className="rounded-lg border border-tinta-200 px-2.5 py-1 text-xs text-tinta-700 hover:bg-tinta-100 disabled:opacity-40"
                      title={usuario?.id === p.id ? 'Você não pode desativar a si mesmo' : undefined}
                    >
                      {p.ativo ? 'Desativar' : 'Reativar'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Cartao>

      <p className="mt-3 max-w-2xl text-xs text-tinta-400">
        Ninguem consegue ler a senha de ninguem: ela e guardada em hash. Quando voce define uma
        senha nova, as sessoes abertas daquela pessoa caem na hora.
      </p>

      <section className="mt-8 max-w-2xl">
        <h2 className="text-base font-semibold text-tinta-900">Aniversarios</h2>
        <p className="mb-3 mt-1 text-sm text-tinta-400">
          A data aparece no “Meu dia” de quem serve, no mes do aniversario. Dia e mes ficam
          visiveis para a equipe e para os voluntarios; o ano so para admin e editor.
        </p>
        <Cartao>
          {membros.length === 0 ? (
            <p className="text-sm text-tinta-400">Nenhum cadastro de membro ainda.</p>
          ) : (
            <ul className="space-y-1">
              {membros.map((m) => (
                <li
                  key={m.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl bg-tinta-100 px-3 py-2"
                >
                  <span className="min-w-[10rem] flex-1 truncate text-sm font-medium text-tinta-800">
                    {m.nome}
                  </span>
                  <input
                    type="date"
                    defaultValue={m.nascimento ?? ''}
                    max={new Intl.DateTimeFormat('en-CA', {
                      timeZone: 'America/Sao_Paulo',
                    }).format(new Date())}
                    onChange={(e) =>
                      void salvarAniversario(m, e.target.value ? e.target.value : null)
                    }
                    className="rounded-xl border border-tinta-200 bg-white px-3 py-1.5 text-sm"
                  />
                  {!m.nascimento && (
                    <span className="text-xs text-tinta-400">sem data</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Cartao>
      </section>

      {criando && (
        <NovaPessoa
          aoFechar={() => setCriando(false)}
          aoCriar={async (nome) => {
            setCriando(false);
            setAviso(`${nome} agora tem acesso ao sistema.`);
            await carregar();
          }}
        />
      )}

      {trocandoSenhaDe && (
        <DefinirSenha
          pessoa={trocandoSenhaDe}
          aoFechar={() => setTrocandoSenhaDe(null)}
          aoDefinir={async () => {
            const nome = trocandoSenhaDe.nome;
            setTrocandoSenhaDe(null);
            setAviso(`Senha de ${nome} trocada. As sessoes dela cairam.`);
            await carregar();
          }}
        />
      )}
      {dialogo}
    </Layout>
  );
}

function NovaPessoa({
  aoFechar,
  aoCriar,
}: {
  aoFechar: () => void;
  aoCriar: (nome: string) => Promise<void>;
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState('editor');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  return (
    <Modal aoFechar={aoFechar} titulo="Nova pessoa">
      {erro && <Alerta>{erro}</Alerta>}
      <div className="mt-3 space-y-3">
        <Campo rotulo="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
        <Campo
          rotulo="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-tinta-700">Papel</span>
          <select
            value={papel}
            onChange={(e) => setPapel(e.target.value)}
            className="w-full rounded-xl border border-tinta-200 bg-white px-3.5 py-2.5"
          >
            {PAPEIS.map((p) => (
              <option key={p} value={p}>
                {NOME_DO_PAPEL[p]}
              </option>
            ))}
          </select>
        </label>
        <p className="rounded-xl bg-tinta-100 px-3 py-2 text-xs text-tinta-600">
          Esta tela cria acesso à operação. Quem vai <strong>servir</strong> se cadastra em
          Escalas, no ministério dele — lá a ficha e a conta nascem ligadas.
        </p>
        <Campo
          rotulo="Senha inicial"
          type="text"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          placeholder="minimo 8 caracteres"
        />
        <button
          type="button"
          onClick={() => setSenha(gerarSenha())}
          className="text-xs text-tinta-400 underline hover:text-tinta-900"
        >
          gerar senha
        </button>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao
          carregando={salvando}
          onClick={async () => {
            setErro(null);
            setSalvando(true);
            try {
              await criarPessoa({
                nome: nome.trim(),
                email: email.trim(),
                papel,
                senha,
              });
              await aoCriar(nome.trim());
            } catch (e) {
              setErro(e instanceof ErroApi ? e.message : 'Não consegui criar.');
            } finally {
              setSalvando(false);
            }
          }}
        >
          Criar acesso
        </Botao>
      </div>
    </Modal>
  );
}

function DefinirSenha({
  pessoa,
  aoFechar,
  aoDefinir,
}: {
  pessoa: Pessoa;
  aoFechar: () => void;
  aoDefinir: () => Promise<void>;
}) {
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  return (
    <Modal aoFechar={aoFechar} titulo={`Definir senha de ${pessoa.nome}`}>
      {erro && <Alerta>{erro}</Alerta>}
      <p className="mt-2 text-sm text-tinta-600">
        Escreva a senha nova e passe para a pessoa. Ninguem consegue ver a senha antiga — ela e
        guardada em hash.
      </p>
      <div className="mt-3">
        <Campo
          rotulo="Nova senha"
          type="text"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          placeholder="minimo 8 caracteres"
        />
        <button
          type="button"
          onClick={() => setSenha(gerarSenha())}
          className="mt-2 text-xs text-tinta-400 underline hover:text-tinta-900"
        >
          gerar senha
        </button>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao
          carregando={salvando}
          onClick={async () => {
            setErro(null);
            setSalvando(true);
            try {
              await definirSenha(pessoa.id, senha);
              await aoDefinir();
            } catch (e) {
              setErro(e instanceof ErroApi ? e.message : 'Não consegui definir a senha.');
            } finally {
              setSalvando(false);
            }
          }}
        >
          Definir senha
        </Botao>
      </div>
    </Modal>
  );
}




